'use client';
import React, { useCallback, useEffect, useState } from 'react';
import Notification from '@/component/Notification';
import { acquireModalSession, releaseModalSession, subscribeModalSession } from '@/lib/client/modalSession';
import DealerLeadModal from './DealerLeadModal';
import { requestMobileLeadKeyboardForNextForm } from './LeadForm';
import { flushSync } from 'react-dom';
import DealerModelSelectModal, { DealerSelectableModel } from './DealerModelSelectModal';

const CONTACT_SUPPORT_MODAL_SESSION_ID = 'dealer-lead-modal';
const MODEL_SELECT_MODAL_SESSION_ID = 'dealer-model-select-modal';

interface DealerLeadButtonProps {
    btnText: string;
    fullWidth?: boolean;
    className?: string;
    dealer_id?: string;
    brandName?: string;
    models: DealerSelectableModel[];
}

const DealerLeadButton: React.FC<DealerLeadButtonProps> = ({ btnText, fullWidth, className, dealer_id, brandName, models = [] }) => {
    const [showModelSelectModal, setShowModelSelectModal] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedModel, setSelectedModel] = useState<DealerSelectableModel | null>(null);
    const [notification, setNotification] = useState<{
        message: string;
        isError: boolean;
        visible: boolean;
    }>({
        message: '',
        isError: false,
        visible: false,
    });

    const closeModelSelectModal = useCallback(() => {
        setShowModelSelectModal(false);
        releaseModalSession(MODEL_SELECT_MODAL_SESSION_ID);
    }, []);

    const closeModal = useCallback(() => {
        setShowModal(false);
        releaseModalSession(CONTACT_SUPPORT_MODAL_SESSION_ID);
    }, []);

    const openModelSelectModal = () => {
        acquireModalSession(MODEL_SELECT_MODAL_SESSION_ID);
        setShowModelSelectModal(true);
    };

    const openModal = () => {
        requestMobileLeadKeyboardForNextForm();
        flushSync(() => {
            acquireModalSession(CONTACT_SUPPORT_MODAL_SESSION_ID);
            setShowModal(true);
        });
    };

    useEffect(() => {
        const unsubscribe = subscribeModalSession((activeId) => {
            if (activeId !== MODEL_SELECT_MODAL_SESSION_ID) {
                setShowModelSelectModal(false);
            }
            if (activeId !== CONTACT_SUPPORT_MODAL_SESSION_ID) {
                setShowModal(false);
            }
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        return () => {
            releaseModalSession(MODEL_SELECT_MODAL_SESSION_ID);
            releaseModalSession(CONTACT_SUPPORT_MODAL_SESSION_ID);
        };
    }, []);

    // Central notification handler
    const showNotification = (message: string, isError = false) => {
        setNotification({ message, isError, visible: true });
        setTimeout(() => {
            setNotification((prev) => ({ ...prev, visible: false }));
        }, 3000);
    };

    const handleLeadSubmit = () => {
        closeModal();
    };

    return (
        <>
            <button
                data-testid="car-lead-cta-button"
                onClick={openModelSelectModal}
                title={btnText ? String(btnText) : undefined}
                className={
                    className
                        ? className
                        : `${fullWidth ? 'w-full' : 'w-1/2'} border-2 text-automotive-red card-btn font-semibold cursor-pointer transition duration-500 ease-in-out hover:text-white py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`
                }
            >
                {btnText}
            </button>

            <DealerModelSelectModal
                showModal={showModelSelectModal}
                onClose={closeModelSelectModal}
                onSelect={(model) => {
                    setSelectedModel(model);
                    closeModelSelectModal();
                    openModal();
                }}
                brandName={brandName}
                models={models}
            />

            <DealerLeadModal
                showModal={showModal}
                onClose={closeModal}
                onSubmit={handleLeadSubmit}
                onError={(msg) => showNotification(msg, true)}
                dealer_id={dealer_id}
                selectedModel={selectedModel}
            />

            <Notification
                message={notification.message}
                isVisible={notification.visible}
                isError={notification.isError}
            />
        </>
    );
};

export default DealerLeadButton;
